import { setCookie, deleteCookie } from "cookies-next";
import { IUserInfo } from "@/props/IUser";

/**
 * cookie 统一安全属性。
 *
 * 三个 cookie（token / user / userInfo）原先都用 cookies-next 的默认选项写入，
 * 既没有 SameSite 也没有 Secure，等于允许跨站请求携带、允许明文信道传输。
 *
 * 注意两点：
 * 1. httpOnly 在这里做不到 —— 客户端 JS 无法设置该属性，且 utils/request.ts 的
 *    请求拦截器要 getCookie("token") 取值拼 Authori-zation 头。要真正 httpOnly，
 *    必须改由后端 Set-Cookie 下发，属后端改动。
 * 2. secure 必须按协议动态判断 —— 本地 dev 跑在 http://localhost:3000，
 *    写死 secure:true 会让 cookie 直接写不进去，登录态整个失效。
 */
export function cookieOptions() {
  const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";
  return {
    // 与 cookies-next 的默认值一致（其 setCookie 内部即 { path: "/", ...options }），
    // 显式写出只为可读，不改变原有行为 —— 应用挂在 basePath(/pointsmall) 下，
    // 但 cookie 一直是写在根 path 的，basePath 不参与 cookie 作用域。
    path: "/",
    sameSite: "lax" as const,
    ...(isHttps && { secure: true }),
  };
}

/**
 * userInfo cookie 的字段白名单。
 *
 * getSession() 返回的是完整用户对象（见 props/IUser.ts 的 IUserInfo，90+ 字段，
 * 含 documentNum / birthday / uphone / address / lastip / password / googleSecret
 * 等敏感项）。整体 JSON.stringify 进 cookie 会让这些字段：
 *   - 被页面上任意 JS 读到；
 *   - 随每个同域请求（含静态资源）自动上行；
 *   - 落进浏览器磁盘与中间层访问日志。
 *
 * 这里只保留页面实际读取的字段。改动 userInfo 消费点时同步维护本表 ——
 * 漏加字段的表现是「刷新后该字段变 undefined」（Header 会用 cookie 重建 Redux 态）。
 */
const USER_INFO_COOKIE_FIELDS = [
  "uuid", // Header/Index.tsx:133、pages/user/index.tsx:129、CSR/Leaderboard.tsx:22、QuickTracking/Index.tsx:35
  "uname", // pages/user/index.tsx:129
  "localName", // CSR/Certificate.tsx:120、CSR/DonationInfoConfirmModal.tsx:35
  "uemail", // CSR/DonationInfoConfirmModal.tsx:36
  "utype", // Header/Index.tsx:126、User/Points/MyPoints.tsx:25、pages/about/exchange、pages/csr
  "canSwitchUser", // Header/Index.tsx:124、User/Points/MyPoints.tsx:102、pages/user/index.tsx:133
  "clientUuid", // Modal/PointsTransferModal.tsx:140
  "ibUuid", // Modal/PointsTransferModal.tsx:140
  "nationality", // pages/order/confirm.tsx:52
  "platform", // Modal/KYCTipModal.tsx:15
  "fxboKycPassed", // Modal/KYCTipModal.tsx:15
  "bssKycPassed", // Modal/KYCTipModal.tsx:15
  "hideUemail", // Modal/OTPModal.tsx:35、Order/Confirm/OrderActionButtons.tsx:134
  "hideUphone", // 同上
] as const;

/**
 * 写 userInfo cookie 的唯一入口：白名单裁剪 + 安全属性。
 *
 * 脏值直接跳过不写 —— getSession() 被 401 拦截器吞掉时会返回 undefined，
 * 原先各处 JSON.stringify(undefined) 会把 "undefined" 字符串写进 cookie，
 * 后续 JSON.parse 直接整页崩溃（Header/Index.tsx:21 的防御注释即为此）。
 */
export function setUserInfoCookie(userInfo: IUserInfo | any) {
  if (!userInfo || typeof userInfo !== "object") return;

  const picked: Record<string, any> = {};
  USER_INFO_COOKIE_FIELDS.forEach((key) => {
    if (userInfo[key] !== undefined) picked[key] = userInfo[key];
  });

  setCookie("userInfo", JSON.stringify(picked), cookieOptions());
}

/** 登录态相关的三个 cookie，登出 / 401 / 跨站跳转时要一起清 */
const AUTH_COOKIES = ["token", "user", "userInfo"] as const;

/**
 * 清空登录态 cookie。三处调用点（request 拦截器 401 / 登出 / 跨站跳转）行为完全一致，
 * 收在一起避免漏删某一个。
 *
 * 与改动前逐个 deleteCookie 语义相同：cookies-next 的 deleteCookie 内部就是
 * setCookie(key, "", { maxAge: -1 })，同样默认 path:"/"，与写入时的 path 对齐。
 */
export function clearAuthCookies() {
  AUTH_COOKIES.forEach((name) => deleteCookie(name));
}
